import useMediaQuery from '@/hooks/use-media-query'
import OrderService from '@/services/Order/OrderService'
import { OrderType } from '@/types/OrderType'
import { getMessageErrorSubmit } from '@/utils/Functions'
import { flashStore } from '@/utils/Store'
import * as Sentry from '@sentry/react'
import { AxiosResponse } from 'axios'
import { Edit } from 'lucide-react'
import React, { useState } from 'react'
import { Button, Card, Form, Spinner, Table } from 'react-bootstrap'

interface ApiResponse {
  response: AxiosResponse
  violations: Violation[]
}

interface Violation {
  propertyPath: string
  message: string
  code: string
}

type Props = {
  order: OrderType | undefined
}

// type Input = {
//   barcode: string
//   cleveronSlotId: string
//   multiOrderCode: string
//   receiveCode: string
//   hold: string
// }

const MD_BREAKPOINT = '(min-width: 769px)'

export const OrderDetailCard: React.FC<Props> = ({ order }: Props) => {
  const isDesktop = useMediaQuery(MD_BREAKPOINT)
  /* States
   *******************************************************************************************/
  const [onSubmitLoading, setOnSubmitLoading] = React.useState<boolean>(false)
  const [isEditing, setIsEditing] = useState<boolean>(false)
  const [messageErrorInput, setMessageErrorInput] = React.useState()
  const [data, setData] = useState<OrderType | undefined>(order)
  const flash = flashStore()

  /* UseEffect
   *******************************************************************************************/
  React.useEffect(() => {
    setData(order)
  }, [order])

  /* Functions
   *******************************************************************************************/
  // Function to handle input changes in the form fields
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target
    setData((prevData) => {
      if (!prevData) return prevData
      return { ...prevData, [name]: name === 'hold' ? Number(value) : value }
    })
  }

  // Function to handle form submission
  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    setOnSubmitLoading(true)
    if (data?.id !== undefined) {
      OrderService.update(data.id, {
        cleveronOrderId: Number(data?.cleveronOrderId),
        externalOrderId: data?.externalOrderId,
        barcode: data?.barcode,
        cleveronSlotId: Number(data?.cleveronSlotId),
        multiOrderCode: data?.multiOrderCode,
        receiveCode: data?.receiveCode,
        hold: data?.hold,
        ageRestriction: data?.ageRestriction ? Number(data?.ageRestriction) : null,
      })
        .then(() => {
          flash.setMessage('success', 'Les données ont bien été modifiées !')
          setIsEditing(false)
          setOnSubmitLoading(false)
        })
        .catch((error: ApiResponse) => {
          setOnSubmitLoading(false)
          if (error?.response?.data?.violations?.length > 0) {
            flash.setMessageErrorValidator()
            setMessageErrorInput(error.response.data.violations)
          } else {
            flash.setMessageErrorForm()
            Sentry.captureException(error)
          }
        })
    }
  }

  /* Return
   *******************************************************************************************/
  return (
    <Card className='mb-4'>
      <Card.Header className='d-flex justify-content-between align-items-center'>
        <h5 className='mt-2'>Détails supplémentaires</h5>
        <Edit className='cursor-pointer' onClick={() => setIsEditing(!isEditing)} />
      </Card.Header>
      <Card.Body className='p-md-0'>
        {isDesktop ? (
  <>
        {!isEditing ? (
          <Table>
            <thead>
              <tr>
                <th>Clev. Order ID</th>
                <th>Slot</th>
                <th>Barcode</th>
                <th>Multi Order Code</th>
                <th>Receive Code</th>
                <th>Hold Time</th>
                <th>Age restriction</th>
                <th>Ext. Order ID</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>{data?.cleveronOrderId}</td>
                <td>{data?.cleveronSlotId}</td>
                <td>{data?.barcode}</td>
                <td>{data?.multiOrderCode}</td>
                <td>{data?.receiveCode}</td>
                <td>{data?.hold}</td>
                <td>{data?.ageRestriction ?? '-'}</td>
                <td>{data?.externalOrderId}</td>
              </tr>
            </tbody>
          </Table>
        ) : (
          <Form onSubmit={handleSubmit}>
            <Table>
              <thead>
                <tr>
                  <th>Clev. Order ID</th>
                  <th>Slot</th>
                  <th>Barcode</th>
                  <th>Multi Order Code</th>
                  <th>Receive Code</th>
                  <th>Hold Time</th>
                  <th>Age restriction</th>
                  <th>Ext. Order ID</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>
                    <Form.Control
                      type='text'
                      name='cleveronOrderId'
                      value={data?.cleveronOrderId || ''}
                      onChange={handleInputChange}
                    />
                    {messageErrorInput && (
                      <small className='text-danger'>
                        {getMessageErrorSubmit(messageErrorInput, 'cleveronOrderId')}
                      </small>
                    )}
                  </td>
                  <td>
                    <Form.Control
                      type='text'
                      name='cleveronSlotId'
                      value={data?.cleveronSlotId || ''}
                      onChange={handleInputChange}
                    />
                    {messageErrorInput && (
                      <small className='text-danger'>
                        {getMessageErrorSubmit(messageErrorInput, 'cleveronSlotId')}
                      </small>
                    )}
                  </td>
                  <td>
                    <Form.Control
                      type='text'
                      name='barcode'
                      value={data?.barcode || ''}
                      onChange={handleInputChange}
                    />
                    {messageErrorInput && (
                      <small className='text-danger'>
                        {getMessageErrorSubmit(messageErrorInput, 'barcode')}
                      </small>
                    )}
                  </td>
                  <td>
                    <Form.Control
                      type='text'
                      name='multiOrderCode'
                      value={data?.multiOrderCode || ''}
                      onChange={handleInputChange}
                    />
                    {messageErrorInput && (
                      <small className='text-danger'>
                        {getMessageErrorSubmit(messageErrorInput, 'multiOrderCode')}
                      </small>
                    )}
                  </td>
                  <td>
                    <Form.Control
                      type='text'
                      name='receiveCode'
                      value={data?.receiveCode || ''}
                      onChange={handleInputChange}
                    />
                    {messageErrorInput && (
                      <small className='text-danger'>
                        {getMessageErrorSubmit(messageErrorInput, 'receiveCode')}
                      </small>
                    )}
                  </td>
                  <td>
                    <Form.Control
                      type='text'
                      name='hold'
                      value={data?.hold || ''}
                      onChange={handleInputChange}
                    />
                    {messageErrorInput && (
                      <small className='text-danger'>
                        {getMessageErrorSubmit(messageErrorInput, 'hold')}
                      </small>
                    )}
                  </td>
                  <td>
                    <Form.Control
                      type='text'
                      name='ageRestriction'
                      value={data?.ageRestriction || ''}
                      onChange={handleInputChange}
                    />
                    {messageErrorInput && (
                      <small className='text-danger'>
                        {getMessageErrorSubmit(messageErrorInput, 'ageRestriction')}
                      </small>
                    )}
                  </td>
                  <td>
                    <Form.Control
                      type='text'
                      name='externalOrderId'
                      value={data?.externalOrderId || ''}
                      onChange={handleInputChange}
                    />
                    {messageErrorInput && (
                      <small className='text-danger'>
                        {getMessageErrorSubmit(messageErrorInput, 'externalOrderId')}
                      </small>
                    )}
                  </td>
                </tr>
              </tbody>
            </Table>
            <div className='d-flex justify-content-end mt-3 pb-4 pe-4'>
              <Button
                variant='secondary'
                type='submit'
                className='me-3'
                onClick={() => setIsEditing(false)}
              >
                Annuler
              </Button>
              <Button variant='dark' type='submit'>
                {onSubmitLoading ? <Spinner size='sm' /> : 'Enregistrer'}
              </Button>
            </div>
          </Form>
        )}
          </>
        
        
        ) : (
        <Form>
         
            <Form.Label htmlFor='cleveronOrderId' className='mb-0 fw-bold'>Clever</Form.Label>
            <Form.Control
              type='text'
              name='cleveronOrderId'
              value={data?.cleveronOrderId || ''}
              onChange={handleInputChange}
              readOnly={!isEditing}
              className={!isEditing ? "border-0 bg-light-dark ps-2 mb-3" : "mb-3"}
            />
            {messageErrorInput && (
              <small className='text-danger'>
                {getMessageErrorSubmit(messageErrorInput, 'cleveronOrderId')}
              </small>
            )}
          
          <div>
            <Form.Label htmlFor='cleveronSlotId' className='mb-0 fw-bold'>Slot</Form.Label>
            <Form.Control
              type='text'
              name='cleveronSlotId'
              value={data?.cleveronSlotId || ''}
              onChange={handleInputChange}
              readOnly={!isEditing}
              className={!isEditing ? "border-0 bg-light-dark ps-2 mb-3" : " mb-3"}
            />
            {messageErrorInput && (
              <small className='text-danger'>
                {getMessageErrorSubmit(messageErrorInput, 'cleveronSlotId')}
              </small>
            )}
          </div>
          <div>
            <Form.Label htmlFor='barcode' className='mb-0 fw-bold'>Barcode</Form.Label>
            <Form.Control
              type='text'
              name='barcode'
              value={data?.barcode || ''}
              onChange={handleInputChange}
              readOnly={!isEditing}
              className={!isEditing ? "border-0 bg-light-dark ps-2 mb-3" : "mb-3"}
            />
            {messageErrorInput && (
              <small className='text-danger'>
                {getMessageErrorSubmit(messageErrorInput, 'barcode')}
              </small>
            )}
          </div>
          <div>
            <Form.Label htmlFor='multiOrderCode' className='mb-0 fw-bold'>Multi Order Code</Form.Label>
            <Form.Control
              type='text'
              name='multiOrderCode'
              value={data?.multiOrderCode || ''}
              onChange={handleInputChange}
              readOnly={!isEditing}
              className={!isEditing ? "border-0 bg-light-dark ps-2 mb-3" : "mb-3"}
            />
            {messageErrorInput && (
              <small className='text-danger'>
                {getMessageErrorSubmit(messageErrorInput, 'multiOrderCode')}
              </small>
            )}
          </div>
          <div>
            <Form.Label htmlFor='receiveCode' className='mb-0 fw-bold'>receive Code</Form.Label>

            <Form.Control
              type='text'
              name='receiveCode'
              value={data?.receiveCode || ''}
              onChange={handleInputChange}
              readOnly={!isEditing}
              className={!isEditing ? "border-0 bg-light-dark ps-2 mb-3" : "mb-3"}
            />
            {messageErrorInput && (
              <small className='text-danger'>
                {getMessageErrorSubmit(messageErrorInput, 'receiveCode')}
              </small>
            )}
          </div>
          <div>
            <Form.Label htmlFor='hold' className='mb-0 fw-bold'>Hold Time</Form.Label>

            <Form.Control
              type='text'
              name='hold'
              value={data?.hold || ''}
              onChange={handleInputChange}
              readOnly={!isEditing}
              className={!isEditing ? "border-0 bg-light-dark ps-2 mb-3" : "mb-3"}
            />
            {messageErrorInput && (
              <small className='text-danger'>
                {getMessageErrorSubmit(messageErrorInput, 'hold')}
              </small>
            )}
          </div>
          <div>
            <Form.Label htmlFor='ageRestriction' className='mb-0 fw-bold'>Age Restriction</Form.Label>

            <Form.Control
              type='text'
              name='ageRestriction'
              value={data?.ageRestriction || ''}
              onChange={handleInputChange}
              readOnly={!isEditing}
              className={!isEditing ? "border-0 bg-light-dark ps-2 mb-3" : "mb-3"}
            />
            {messageErrorInput && (
              <small className='text-danger'>
                {getMessageErrorSubmit(messageErrorInput, 'ageRestriction')}
              </small>
            )}
          </div>
          <div>
            <Form.Label htmlFor='externalOrderId' className='mb-0 fw-bold'>Ext. Order Id</Form.Label>

            <Form.Control
              type='text'
              name='externalOrderId'
              value={data?.externalOrderId || ''}
              onChange={handleInputChange}
              readOnly={!isEditing}
              className={!isEditing ? "border- border-bottom bg-light-dark ps-2 mb-3" : "mb-3"}
            />
            {messageErrorInput && (
              <small className='text-danger'>
                {getMessageErrorSubmit(messageErrorInput, 'externalOrderId')}
              </small>
            )}
          </div>
            {isEditing && (
              <div className='d-flex justify-content-end mt-3 pb-4 pe-4'>
            <Button
              variant='secondary'
              className='me-3'
              onClick={() => setIsEditing(false)}
              >
              Annuler
            </Button>
            <Button variant='dark' type='submit'>
              {onSubmitLoading ? <Spinner size='sm' /> : 'Enregistrer'}
            </Button>
          </div>
            )}
        </Form>

        ) }

      </Card.Body>
    </Card>
  )
}
